import React, {useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {css} from 'emotion';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import SiteDetailsTable from './SiteDetailsTable';
import {ConstraintValueRecord, DateIdMap} from '../utils/analyzeConstraints';
import {getConstraint} from '../../../common/apis/BackendApisClient';
import {useAppSelector} from '../../../app/hooks';
import {selectCachedBusiness, selectCachedCountry, selectCachedRegion} from '../../cached-state/cachedStateSlice';
import {getLocalizedAPIsStartDate, getLocalizedDateTimeStrings} from '../../../common/utils/dates';
import PageLoading from '../../../common/components/PageLoading';

const TOTAL_DAYS = 91; // 13 weeks
const DAYS_PER_PAGE = 14;
const MAX_PAGE_OFFSET = 12;

interface IProps {
  history: RouteComponentProps['history'];
  pitConstraints: ConstraintValueRecord[];
  highlightedDates: DateIdMap;
  siteId: string;
  numSlots?: number;
  firstSlot?: number;
  windowConstraintId?: string;
}

const toggleRowStyle = css`
  padding-bottom: 8px;
`;

const SitePITConstraintTable = ({history, pitConstraints, highlightedDates, siteId}: IProps) => {
  const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
  const [perSlotDailyData, setPerSlotDailyData] = useState<ConstraintValueRecord[] | null>(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [dayOffset, setDayOffset] = useState(0);

  const businessType = useAppSelector(selectCachedBusiness);
  const region = useAppSelector(selectCachedRegion);
  const country = useAppSelector(selectCachedCountry);

  const currDate = new Date();
  const startTime = getLocalizedAPIsStartDate(currDate);

  // Fetch per-slot data from the constraint endpoint when switching to day view
  useEffect(() => {
    if (viewMode === 'day' && !perSlotDailyData && businessType && region) {
      setLoadingDaily(true);

      const fetchPerSlotDaily = async () => {
        const rows: ConstraintValueRecord[] = [];

        for (const constraint of pitConstraints) {
          const response = await getConstraint({
            businessType: businessType!,
            region: region!,
            country: country ?? undefined,
            constraintId: Number(constraint.constraintId),
            startTime,
            query: siteId,
          });

          if (response && response.valuesData && response.valuesData.length > 0) {
            const siteRow = response.valuesData.find((r) => r.siteId === siteId);
            if (siteRow) {
              // Expand to daily with forward-fill
              const dailyRecord: ConstraintValueRecord = {
                constraintId: constraint.constraintId,
                constraintName: constraint.constraintName,
                constraintValueType: constraint.constraintValueType as string,
                constraintUsageTags: constraint.constraintUsageTags as string[],
                constraint: constraint.constraint,
              };

              const startDate = new Date(startTime);
              let lastValue: string | undefined;

              for (let i = 0; i < TOTAL_DAYS; i++) {
                const d = new Date(startDate);
                d.setDate(d.getDate() + i);
                const dayKey = getLocalizedDateTimeStrings(d).date;

                // Check if there's a direct value for this day
                if (siteRow[dayKey] !== undefined && siteRow[dayKey] !== null && siteRow[dayKey] !== '') {
                  lastValue = siteRow[dayKey];
                } else {
                  // Check week start key
                  const weekStart = new Date(d);
                  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                  const weekKey = getLocalizedDateTimeStrings(weekStart).date;
                  if (siteRow[weekKey] !== undefined && siteRow[weekKey] !== null && siteRow[weekKey] !== '') {
                    lastValue = siteRow[weekKey];
                  }
                }

                // Forward-fill: use the last known value
                if (lastValue !== undefined) {
                  dailyRecord[dayKey] = lastValue;
                }
              }

              rows.push(dailyRecord);
            }
          }
        }

        setPerSlotDailyData(rows);
        setLoadingDaily(false);
      };

      fetchPerSlotDaily();
    }
  }, [viewMode, perSlotDailyData, pitConstraints, businessType, region, country, siteId, startTime]);

  if (!pitConstraints || pitConstraints.length === 0) {
    return null;
  }

  // For daily view, slice to current page
  const getPagedDailyData = (): ConstraintValueRecord[] | null => {
    if (!perSlotDailyData) return null;

    const startDate = new Date(startTime);
    startDate.setDate(startDate.getDate() + dayOffset * 7);

    const pageDayKeys: string[] = [];
    for (let i = 0; i < DAYS_PER_PAGE; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      pageDayKeys.push(getLocalizedDateTimeStrings(d).date);
    }

    return perSlotDailyData.map((row) => {
      const pagedRow: ConstraintValueRecord = {
        constraintId: row.constraintId,
        constraintName: row.constraintName,
        constraintValueType: row.constraintValueType as string,
        constraintUsageTags: row.constraintUsageTags as string[],
        constraint: row.constraint,
      };
      pageDayKeys.forEach((key) => {
        if (row[key] !== undefined) {
          pagedRow[key] = row[key];
        }
      });
      return pagedRow;
    });
  };

  // Date range label for daily view
  const rangeStartDate = new Date(startTime);
  rangeStartDate.setDate(rangeStartDate.getDate() + dayOffset * 7);
  const rangeEndDate = new Date(rangeStartDate);
  rangeEndDate.setDate(rangeEndDate.getDate() + DAYS_PER_PAGE - 1);
  const rangeLabel = `${rangeStartDate.toLocaleString('default', {month: 'short', day: '2-digit'})} - ${rangeEndDate.toLocaleString('default', {month: 'short', day: '2-digit'})}`;

  if (viewMode === 'day' && loadingDaily) {
    return (
      <div>
        <Row className={toggleRowStyle} spacing="small">
          <Button type="tertiary" size="small" onClick={() => { setViewMode('week'); setDayOffset(0); }}>Weekly Aggregate</Button>
          <Button type="primary" size="small">Per Slot (Daily)</Button>
        </Row>
        <Row className={toggleRowStyle} spacing="small" alignmentVertical="center">
          <Button type="tertiary" size="small" disabled>← Prev</Button>
          <Text type="b200">{rangeLabel}</Text>
          <Button type="tertiary" size="small" disabled>Next →</Button>
        </Row>
        <PageLoading />
      </div>
    );
  }

  const displayData = viewMode === 'week' ? pitConstraints : getPagedDailyData();

  if (!displayData) {
    return null;
  }

  return (
    <div>
      <Row className={toggleRowStyle} spacing="small">
        <Button
          type={viewMode === 'week' ? 'primary' : 'tertiary'}
          size="small"
          onClick={() => { setViewMode('week'); setDayOffset(0); }}
        >
          Weekly Aggregate
        </Button>
        <Button
          type={viewMode === 'day' ? 'primary' : 'tertiary'}
          size="small"
          onClick={() => setViewMode('day')}
        >
          Per Slot (Daily)
        </Button>
      </Row>
      {viewMode === 'day' && (
        <Row className={toggleRowStyle} spacing="small" alignmentVertical="center">
          <Button type="tertiary" size="small" onClick={() => setDayOffset(Math.max(0, dayOffset - 2))} disabled={dayOffset === 0}>
            ← Prev
          </Button>
          <Text type="b200">{rangeLabel}</Text>
          <Button type="tertiary" size="small" onClick={() => setDayOffset(dayOffset + 2)} disabled={dayOffset >= MAX_PAGE_OFFSET}>
            Next →
          </Button>
        </Row>
      )}
      <SiteDetailsTable
        key={`${viewMode}-${dayOffset}`}
        history={history}
        allowSorting={false}
        allowPaging={false}
        allowFiltering={false}
        constraintData={displayData}
        highlightedDates={highlightedDates}
        summaryRecords={[]}
      />
    </div>
  );
};

export default SitePITConstraintTable;
